import api from './api';
import type { OrdineRMA, RMARequest } from '../types';

/** Servizio RMA — collega il flusso resi ai dati reali (ordini ERP + pratiche persistite). */

/** Chi spedisce la merce resa (INT-129): soglia e testi stanno solo nel
 *  backend (`services/rma_spedizione.py`). Una pratica li porta già in
 *  `spedizioneReso`; il carrello resi, che la pratica non l'ha ancora, li
 *  chiede qui. */
export interface RegolaSpedizioneReso {
  soglia: number;
  testoSotto: string;
  testoSopra: string;
}

export async function fetchRegolaSpedizioneReso(): Promise<RegolaSpedizioneReso> {
  const { data } = await api.get<RegolaSpedizioneReso>('/api/clienti/me/rma/spedizione-reso');
  return data;
}

export async function fetchMyRMAs(): Promise<RMARequest[]> {
  const { data } = await api.get<{ items: RMARequest[] }>('/api/clienti/me/rma');
  return data.items;
}

export async function fetchMyRMA(numero: string): Promise<RMARequest> {
  const { data } = await api.get<RMARequest>(`/api/clienti/me/rma/${encodeURIComponent(numero)}`);
  return data;
}

/** L'Ordine RMA della pratica (404 finché non è chiusa con pezzi approvati). */
export async function fetchMyRMAOrdine(numero: string): Promise<OrdineRMA> {
  const { data } = await api.get<OrdineRMA>(`/api/clienti/me/rma/${encodeURIComponent(numero)}/ordine`);
  return data;
}

export async function fetchQuantitaRese(tipork: string, anno: number, serie: string, numdoc: number): Promise<Record<string, number>> {
  const { data } = await api.get<{ quantitaRese: Record<string, number> }>('/api/clienti/me/rma/quantita-rese', {
    params: { tipork, anno, serie, numdoc },
  });
  return data.quantitaRese;
}

export interface RMACreateUnit {
  id: string;
  motivo: string;
  descrizione?: string;
  photoPath?: string | null;  // chiave dell'allegato, da ``uploadRMAAttachment``
  photoUrl?: string | null;   // solo per i carrelli salvati prima dell'Object Storage
  photoName?: string | null;
  photoMime?: string | null;
}

export interface RMACreateItem {
  sku: string;
  title: string;
  brand?: string;
  category?: string;
  orderId: string;
  orderDate?: string;
  orderStatus?: string;
  docTipork?: string;
  docAnno?: number;
  docSerie?: string;
  docNumdoc?: number;
  unitPrice?: number;
  units: RMACreateUnit[];
}

export async function createRMA(items: RMACreateItem[]): Promise<RMARequest> {
  const { data } = await api.post<RMARequest>('/api/clienti/me/rma', { items });
  return data;
}

export async function fetchAdminRMAs(): Promise<RMARequest[]> {
  const { data } = await api.get<{ items: RMARequest[] }>('/api/admin/rma');
  return data.items;
}

export async function fetchAdminRMA(numero: string): Promise<RMARequest> {
  const { data } = await api.get<RMARequest>(`/api/admin/rma/${numero}`);
  return data;
}

export interface AdminRMAUnitPatch {
  decision?: string;
  note?: string;
  adminPhotoPath?: string | null;  // chiave dell'allegato, da ``uploadAdminRMAAttachment``
  adminPhotoName?: string | null;
  adminPhotoMime?: string | null;
}

export async function patchAdminRMAUnit(numero: string, unitId: string, patch: AdminRMAUnitPatch): Promise<RMARequest> {
  const { data } = await api.patch<RMARequest>(`/api/admin/rma/${numero}`, {
    unitUpdate: { unitId, ...patch },
  });
  return data;
}

export async function closeAdminRMA(numero: string): Promise<RMARequest> {
  const { data } = await api.patch<RMARequest>(`/api/admin/rma/${numero}`, { close: true });
  return data;
}

/** Carica una foto/PDF a supporto della decisione dell'operatore (es. respingimento). */
export async function uploadAdminRMAAttachment(file: File): Promise<RMAUploadResult> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<RMAUploadResult>('/api/admin/rma/upload', form, {
    headers: { 'Content-Type': undefined },
  });
  return data;
}

export interface RMAUploadResult {
  path: string;  // chiave nel bucket, da rimandare alla creazione/decisione
  url: string;   // URL presigned per l'anteprima
  name: string;
  mime: string;
}

/** Carica una foto/PDF allegato a un pezzo del reso (nel bucket, prefisso privato). */
export async function uploadRMAAttachment(file: File): Promise<RMAUploadResult> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<RMAUploadResult>('/api/clienti/me/rma/upload', form, {
    headers: { 'Content-Type': undefined },
  });
  return data;
}
